import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Button, Modal } from "react-bootstrap";
import { FaUtensils, FaArrowRight, FaArrowLeft, FaExchangeAlt } from "react-icons/fa";
// Fork, plate and knife on a plain near-white field: the restaurant
// placeholder for a dish that has not been photographed yet.
import dishPlaceholder from "../assets/img/restaurant-dish-placeholder.png";
import "./OutletManager.css";
import { useHeadOfficeGate } from "./team/Gated";

/**
 * OutletMenuItemsModal
 * ─────────────────────────────────────────────────────────────────────────────
 * The one picker for "which dishes does this outlet serve": the restaurant's
 * full menu on the left, the outlet's selection on the right, click to move,
 * Add All / Remove All, Save only when something changed.
 *
 * It used to live inside OutletManager (the vendor's own Outlets tab), which
 * left the admin Restaurants directory with no way to set an outlet's menu
 * except one chip at a time inside View Menu. Now both pages mount THIS and
 * neither carries its own copy.
 *
 * Persistence stays with the caller: `onSave(menuItemIds)` returns a promise.
 * The vendor page dispatches the outlet redux action; the admin directory
 * calls the API and refreshes its list. The modal owns only the selection.
 *
 * The pharmacy console had its own copy of this picker (4 Oct 2026); it now
 * mounts this one with `itemsLabel="Products"` and its own placeholder.
 *
 * @param {boolean}  show
 * @param {object}   outlet     { _id, name, menuItems: [ids] }
 * @param {Array}    sections   the parent's sections, each with products
 * @param {Function} onHide
 * @param {Function} onSave     (menuItemIds: string[]) => Promise<any>
 * @param {string}   [itemsLabel]  "Menu Items" (default) or "Products"
 * @param {string}   [placeholderImage]  shown for an item with no photo
 * @param {string}   [note]     one plain line above the lists, when what this
 *                              list does needs saying (the pharmacy one)
 */
const OutletMenuItemsModal = ({
  show,
  outlet,
  sections,
  onHide,
  onSave,
  itemsLabel = "Menu Items",
  placeholderImage = dishPlaceholder,
  note = null,
}) => {
  // Vendor team permissions: what an outlet sells is outlets:manage, and it
  // is head office's even for a member who runs that branch (4 Oct 2026).
  const outletsGate = useHeadOfficeGate("outlets:manage");
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [originalIds, setOriginalIds] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const allMenuItems = useMemo(() => {
    const items = [];
    (sections || []).forEach((section) => {
      (section?.products || []).forEach((product) => {
        if (product?._id && product?.name) {
          items.push({
            _id: product._id,
            name: product.name,
            sectionName: section.title,
            price: product.price || 0,
            image: product.image || placeholderImage,
            description: product.description,
            enabled: product.enabled,
          });
        }
      });
    });
    return items;
  }, [sections, placeholderImage]);

  // Split the menu into the two lists every time the modal opens for an outlet.
  useEffect(() => {
    if (!show || !outlet) return;
    const outletIds = (outlet.menuItems || []).map((id) =>
      typeof id === "object" && id?._id ? String(id._id) : String(id)
    );
    setSelectedItems(allMenuItems.filter((item) => outletIds.includes(String(item._id))));
    setAvailableItems(allMenuItems.filter((item) => !outletIds.includes(String(item._id))));
    setOriginalIds([...outletIds].sort().join(","));
    setIsSaving(false);
  }, [show, outlet, allMenuItems]);

  const selectedKey = selectedItems.map((item) => String(item._id)).sort().join(",");
  const hasChanges = selectedKey !== originalIds;

  const moveToSelected = (item) => {
    setSelectedItems((prev) => [...prev, item]);
    setAvailableItems((prev) => prev.filter((i) => i._id !== item._id));
  };
  const moveToAvailable = (item) => {
    setAvailableItems((prev) => [...prev, item]);
    setSelectedItems((prev) => prev.filter((i) => i._id !== item._id));
  };
  const moveAllToSelected = () => {
    setSelectedItems((prev) => [...prev, ...availableItems]);
    setAvailableItems([]);
  };
  const moveAllToAvailable = () => {
    setAvailableItems((prev) => [...prev, ...selectedItems]);
    setSelectedItems([]);
  };

  const handleSave = async () => {
    if (!onSave || !hasChanges) return;
    setIsSaving(true);
    try {
      await onSave(selectedItems.map((item) => item._id));
      onHide();
    } catch (error) {
      // The caller reports the failure (toast); the modal stays open with
      // the selection intact so nothing is lost.
      console.error("Error saving outlet menu items:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const money = (amount) =>
    new Intl.NumberFormat("en-UG", { style: "currency", currency: "UGX" }).format(amount);

  return (
    <Modal show={show} onHide={isSaving ? undefined : onHide} size="lg" className="menu-management-modal">
      <Modal.Header closeButton={!isSaving}>
        <Modal.Title className="d-flex align-items-center">
          <FaUtensils className="me-2 text-primary" />
          {itemsLabel} - {outlet?.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {note ? (
          <div className="lk-note lk-note--info" style={{ marginBottom: 12 }}>
            <span>{note}</span>
          </div>
        ) : null}
        <Row>
          <Col md={5}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0 d-flex align-items-center">
                Available Items
                <span className="item-count ms-2">{availableItems.length} items</span>
              </h5>
            </div>
            <div className="menu-list">
              {availableItems.length > 0 ? (
                availableItems.map((item) => (
                  <div
                    key={item._id}
                    className={`menu-item ${isSaving ? "disabled" : ""}`}
                    onClick={() => !isSaving && moveToSelected(item)}
                  >
                    <div className="d-flex align-items-center p-2 border rounded mb-2 hover-item">
                      <img src={item.image || placeholderImage} alt={item.name} className="menu-item-image" />
                      <div className="ms-3 flex-grow-1">
                        <div className="product-name">{item.name}</div>
                        <small className="text-muted d-block">{item.sectionName}</small>
                        <div className="text-primary">{money(item.price)}</div>
                      </div>
                      <FaArrowRight className="ms-3 text-primary" />
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-muted p-3">
                  <p>No available menu items</p>
                  <small>All items have been selected or no items exist</small>
                </div>
              )}
            </div>
          </Col>
          <Col md={2} className="d-flex flex-column align-items-center justify-content-center">
            <div className="bulk-action" onClick={() => !isSaving && moveAllToSelected()}>
              <FaArrowRight className={`text-primary mb-2 ${isSaving ? "text-muted" : ""}`} size={24} />
              <p className={`text-muted small mb-3 ${isSaving ? "opacity-50" : ""}`}>Add All</p>
            </div>
            <div className="middle-section">
              <FaExchangeAlt className="text-muted mb-2" size={20} />
              <p className="text-muted small mb-0">Click on items to move them between lists</p>
            </div>
            <div className="bulk-action" onClick={() => !isSaving && moveAllToAvailable()}>
              <FaArrowLeft className={`text-danger mt-3 ${isSaving ? "text-muted" : ""}`} size={24} />
              <p className={`text-muted small ${isSaving ? "opacity-50" : ""}`}>Remove All</p>
            </div>
          </Col>
          <Col md={5}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0 d-flex align-items-center">
                Selected Items
                <span className="item-count ms-2">{selectedItems.length} items</span>
              </h5>
            </div>
            <div className="menu-list">
              {selectedItems.map((item) => (
                <div
                  key={item._id}
                  className={`menu-item ${isSaving ? "disabled" : ""}`}
                  onClick={() => !isSaving && moveToAvailable(item)}
                >
                  <div className="d-flex align-items-center p-2 border rounded mb-2 hover-item">
                    <FaArrowLeft className="me-2 text-danger" />
                    <img src={item.image || placeholderImage} alt={item.name} className="menu-item-image" />
                    <div className="ms-3">
                      <div className="product-name">{item.name}</div>
                      <small className="text-muted">{item.sectionName}</small>
                      <div className="text-primary">{money(item.price)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer className="border-top-0">
        <Button variant="light" onClick={onHide} className="px-4" disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          className={`px-4 ${isSaving || !hasChanges ? "btn-saving" : ""}`}
          disabled={(isSaving || !hasChanges) || !outletsGate.allowed}
          title={outletsGate.title}
        >
          {isSaving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              <span className="saving-text">Saving...</span>
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OutletMenuItemsModal;
